import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  getAnalyticsConsent,
  isAnalyticsExcludedRoute,
  restoreAnalyticsConsent,
  setAnalyticsConsent,
} from "@/lib/analytics";

export default function AnalyticsConsent() {
  const { pathname } = useLocation();
  const [choice, setChoice] = useState(() => getAnalyticsConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isExcluded = isAnalyticsExcludedRoute(pathname);

  useEffect(() => {
    if (choice === "accepted" && !isExcluded) restoreAnalyticsConsent();
  }, [choice, isExcluded]);

  if (isExcluded) return null;

  const choose = async (nextChoice) => {
    setSaving(true);
    setError("");
    const saved = await setAnalyticsConsent(nextChoice);
    setChoice(getAnalyticsConsent());
    if (!saved) {
      setError("We couldn't save your choice. Analytics will stay off until you can save a preference.");
      setSettingsOpen(true);
    } else {
      setSettingsOpen(false);
    }
    setSaving(false);
  };

  if (choice && !settingsOpen) {
    return (
      <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-50 md:bottom-4">
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-lg hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
          aria-label="Open privacy settings"
          data-testid="analytics-open-settings"
        >
          Privacy settings
        </button>
      </div>
    );
  }

  return (
    <aside
      aria-label="Optional analytics consent"
      className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-50 border-t border-slate-300 bg-white p-4 shadow-[0_-8px_30px_rgba(15,23,42,0.12)] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-lg sm:rounded-xl sm:border"
      data-testid="analytics-consent"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Your privacy choices</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            Allow optional PostHog analytics to help us understand storefront use.
            You can accept or reject it, and change your choice later.
            <Link to="/privacy-policy" className="ml-1 font-medium text-amber-700 underline underline-offset-2">
              Privacy details
            </Link>
          </p>
        </div>
        {choice && (
          <button
            type="button"
            onClick={() => setSettingsOpen(false)}
            className="rounded p-1 text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500"
            aria-label="Close privacy settings"
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-700" role="alert">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => choose("accepted")}
          disabled={saving}
          className="min-h-11 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
          data-testid="analytics-accept"
        >
          Accept analytics
        </button>
        <button
          type="button"
          onClick={() => choose("rejected")}
          disabled={saving}
          className="min-h-11 rounded-lg border border-slate-400 bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
          data-testid="analytics-reject"
        >
          Reject analytics
        </button>
      </div>
    </aside>
  );
}
